import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Grid, TextField } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState, ReactNode, useCallback, FC, useEffect, ChangeEvent, Fragment } from 'react';
import { Label, WsColors } from 'wisestep-base';

export interface IShareType {
  name: string;
  email: string;
}

interface IShareNameAndEmailInput {
  initialValue: IShareType[];
  onInputChange(args: IShareType[]): void;
  reset: () => void;
}

const ShareNameAndEmailInput: FC<IShareNameAndEmailInput> = ({
  initialValue = [{ name: '', email: '' }],
  onInputChange,
  reset,
}) => {
  const [linksValue, setLinksValue] = useState<IShareType[]>(initialValue);
  const [totalLinks, setTotalLinks] = useState<ReactNode[]>([]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number, field: 'email' | 'name') => {
      setLinksValue((linksValue) => {
        linksValue[index][field] = event.target.value;
        const newValues = [...linksValue];
        onInputChange(newValues);
        return newValues;
      });
    },
    [onInputChange]
  );

  const addShareInput = () => {
    if (isEmpty(totalLinks)) {
      setTotalLinks([shareInputWithRemoveIcon(0)]);
    } else {
      setTotalLinks([...totalLinks, shareInputWithRemoveIcon(totalLinks.length)]);
    }
    setLinksValue([...linksValue, { name: '', email: '' }]);
  };

  const removeShareInput = useCallback(
    (index: number) => {
      setTotalLinks((totalLinks) => {
        delete totalLinks[index];
        return [...totalLinks];
      });
      setLinksValue((linksValue) => {
        delete linksValue[index + 1];
        const newValues = [...linksValue];
        onInputChange(newValues);
        return newValues;
      });
      reset();
    },
    [onInputChange, reset]
  );

  const shareInputWithRemoveIcon = useCallback(
    (index: number) => (
      <Fragment key={index}>
        <Grid item xs={6}>
          <Label>Enter Email Address*</Label>
          <TextField
            fullWidth
            defaultValue={linksValue[index + 1]?.email}
            inputProps={{ style: { padding: '11.5px 14px' } }}
            placeholder=""
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'email')}
            variant="outlined"
            autoComplete="candidate_email"
          />
        </Grid>
        <Grid item xs={5.4}>
          <Label>Enter Name*</Label>
          <TextField
            defaultValue={linksValue[index + 1]?.name}
            inputProps={{ style: { padding: '11.5px 14px' } }}
            placeholder=""
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, index + 1, 'name')}
            variant="outlined"
            autoComplete="candidate_name"
          />
        </Grid>
        <Grid item xs={0.6}>
          <RemoveCircleOutlineIcon
            onClick={() => removeShareInput(index)}
            sx={{
              color: WsColors.Red400,
              mt: 5,
              cursor: 'pointer',
            }}
          />
        </Grid>
      </Fragment>
    ),
    [handleChange, linksValue, removeShareInput]
  );

  useEffect(() => {
    if (!isEmpty(linksValue) && isEmpty(totalLinks)) {
      const links: ReactNode[] = [];
      linksValue.forEach((link: IShareType, index: number) => {
        index !== 0 && links.push(shareInputWithRemoveIcon(index - 1));
      });
      if (!isEmpty(links)) {
        setTotalLinks([...links]);
      }
    }
  }, [linksValue, shareInputWithRemoveIcon, totalLinks]);

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={6}>
        <Label>Enter Email Address*</Label>
        <TextField
          fullWidth
          value={linksValue[0]?.email}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder=""
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'email')}
          variant="outlined"
          autoComplete="candidate_email"
        />
      </Grid>
      <Grid item xs={5.4}>
        <Label>Enter Name*</Label>
        <TextField
          value={linksValue[0]?.name}
          inputProps={{ style: { padding: '11.5px 14px' } }}
          placeholder=""
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e, 0, 'name')}
          variant="outlined"
          autoComplete="candidate_name"
        />
      </Grid>
      <Grid item xs={0.6}>
        <AddCircleOutlineIcon
          sx={{ color: WsColors.TheBlues500, mt: 5, cursor: 'pointer' }}
          onClick={() => addShareInput()}
        />
      </Grid>
      {totalLinks.map((component: ReactNode) => component)}
    </Grid>
  );
};

export default ShareNameAndEmailInput;
